import Quill from 'quill';
import Delta from 'quill-delta';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	BoldIcon,
	ChevronDownIcon,
	ItalicIcon,
	JustifyAllIcon,
	JustifyCenterIcon,
	JustifyLeftIcon,
	JustifyRightIcon,
	LinkIcon,
	ListIcon,
	UnderlineIcon,
} from '../../../../treams-icons/src/index';
import { Button } from '../../common/button/Button';
import { IconButton } from '../../common/button/IconButton';
import { IconWrapper } from '../../common/Icon';
import { Paragraph } from '../../common/Text';
import { InlineToolbarDropdown } from './InlineToolbarDropdown';
import { Input } from './Input';

const Toolbar = styled(Box)`
	background: white;
	border: 1px solid #ddd;
	border-radius: 8px;
	box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
	padding: 4px 8px;
	display: flex;
	gap: 8px;
	align-items: 'center';
`;

const Text = styled.span<{ size: 'huge' | 'large' | 'small' | false }>`
	font-weight: ${({ size }) => (size === false ? 600 : 800)};
	font-size: ${({ size }) =>
		size === 'huge' ? '18px' : size === 'large' ? '16px' : size === 'small' ? '12px' : '14px'};
`;

const ALIGN_FORMATS = [false, 'right', 'center', 'justify'] as const;
type AlignValue = (typeof ALIGN_FORMATS)[number];

const SIZE_FORMATS = ['small', false, 'large', 'huge'] as const;
type SizeValue = (typeof SIZE_FORMATS)[number];

type QuillFormatValue = boolean | number | string | null | undefined;

type Props = {
	quill?: Quill;
	onChange?: (value: Delta) => void;
};

export function InlineToolbar({ quill, onChange }: Props) {
	const { t } = useTranslation();

	const toolbarRef = useRef<HTMLDivElement>(null);
	const [url, setUrl] = useState('');

	const [isBold, isItalic, isUnderline, link, alignType] = (() => {
		if (quill) {
			const range = quill.getSelection();
			if (range) {
				const formats = quill.getFormat(range);

				return [
					!!formats['bold'],
					!!formats['italic'],
					!!formats['underline'],
					typeof formats['link'] === 'string' ? formats['link'] : undefined,
					typeof formats['align'] === 'string' ? formats['align'] : false,
				];
			}
		}
		return [false, false, false, undefined, false];
	})();

	const handleFormatClick = (format: string, value: QuillFormatValue) => {
		if (!quill) {
			return;
		}
		const range = quill.getSelection();
		if (!range) {
			return;
		}
		quill.focus();
		quill.setSelection(range);
		quill.format(format, value, 'user');
		// Manually trigger onChange to ensure format changes trigger GraphQL updates
		if (onChange) {
			onChange(quill.getContents() as Delta);
		}
	};

	const handleSizeClick = (value: SizeValue) => {
		handleFormatClick('size', value === false ? false : value);
	};

	const handleAlignClick = (alignValue: AlignValue) => {
		handleFormatClick('align', alignValue === false ? false : alignValue);
	};

	const handleLinkClick = () => {
		handleFormatClick('link', link ?? url);
	};

	const handleListClick = (listType: 'ordered' | 'bullet') => {
		handleFormatClick('list', listType);
	};

	const getAlignIcon = useCallback((alignValue: AlignValue) => {
		const iconMap: Record<'right' | 'center' | 'justify', ReactNode> = {
			right: <JustifyRightIcon />,
			center: <JustifyCenterIcon />,
			justify: <JustifyAllIcon />,
		};
		if (alignValue === false || !alignValue) {
			return <JustifyLeftIcon />;
		}
		return iconMap[alignValue];
	}, []);

	return (
		<Toolbar ref={toolbarRef} onMouseDown={(event) => event.preventDefault()}>
			<IconButton
				type={isBold ? 'primary' : 'plain'}
				icon={<BoldIcon />}
				onClick={() => handleFormatClick('bold', !isBold)}
			/>
			<IconButton
				type={isItalic ? 'primary' : 'plain'}
				icon={<ItalicIcon />}
				onClick={() => handleFormatClick('italic', !isItalic)}
			/>
			<IconButton
				type={isUnderline ? 'primary' : 'plain'}
				icon={<UnderlineIcon />}
				onClick={() => handleFormatClick('underline', !isUnderline)}
			/>

			<InlineToolbarDropdown
				label={
					<Flex alignItems="center">
						<LinkIcon />
						<IconWrapper size={16}>
							<ChevronDownIcon />
						</IconWrapper>
					</Flex>
				}
			>
				<Box minWidth={200} p={2}>
					<Input placeholder={t('common.addLink')} value={url} onChange={setUrl} />
					<Button
						mt={1}
						type="transparent"
						title={t('common.edit')}
						small
						onClick={handleLinkClick}
					/>
				</Box>
			</InlineToolbarDropdown>

			<InlineToolbarDropdown
				label={
					<Flex alignItems="center">
						{getAlignIcon(alignType as AlignValue)}
						<IconWrapper size={16}>
							<ChevronDownIcon />
						</IconWrapper>
					</Flex>
				}
			>
				{ALIGN_FORMATS.map((alignType) => (
					<IconButton
						key={String(alignType)}
						type="plain"
						icon={getAlignIcon(alignType)}
						onClick={() => handleAlignClick(alignType)}
					/>
				))}
			</InlineToolbarDropdown>

			<InlineToolbarDropdown
				label={
					<Flex alignItems="center">
						<Paragraph color="black" fontWeight="bold" fontSize={20}>
							Tt
						</Paragraph>
						<IconWrapper ml={1} size={16}>
							<ChevronDownIcon />
						</IconWrapper>
					</Flex>
				}
			>
				{SIZE_FORMATS.map((size) => (
					<IconButton
						key={String(size)}
						type="plain"
						icon={<Text size={size}>{size === false ? 'Normal' : String(size).toUpperCase()}</Text>}
						onClick={() => handleSizeClick(size)}
					/>
				))}
			</InlineToolbarDropdown>

			<InlineToolbarDropdown
				label={
					<Flex alignItems="center">
						<ListIcon />
						<IconWrapper size={16}>
							<ChevronDownIcon />
						</IconWrapper>
					</Flex>
				}
			>
				<IconButton type="plain" icon={<ListIcon />} onClick={() => handleListClick('ordered')} />
				<IconButton type="plain" icon={<ListIcon />} onClick={() => handleListClick('bullet')} />
			</InlineToolbarDropdown>
		</Toolbar>
	);
}
